import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  canSubmitChangeRequest,
  DOWNLINE_CHANGE_COMMISSION_TYPES,
  DOWNLINE_CHANGE_COMMISSION_TYPE_LABELS,
  DOWNLINE_CHANGE_CONTRACT_TYPES,
  DOWNLINE_CHANGE_CONTRACT_TYPE_LABELS,
  type DownlineChangeCommissionType,
  type DownlineChangeContractType,
} from "@aam/shared";
import { getSessionToken } from "../../hooks/useSession.js";
import { useToast } from "../../context/ToastContext.js";
import { cn } from "../../lib/cn.js";
import {
  Breadcrumbs,
  Button,
  Card,
  EmptyState,
  Label,
  LoadingStatus,
  Select,
  Skeleton,
} from "../ui/index.js";
import { PortalPageIntro } from "./PortalVisuals.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface DownlineRow {
  id: string;
  email: string;
  displayName: string | null;
  phone: string | null;
}

interface ContractOption {
  id: string;
  carrier: string;
  imo: string;
  status: string;
  contractNumber: string | null;
  uplineName: string | null;
  uplineNumber: string | null;
}

interface CarrierOption {
  carrier: string;
  label: string;
  hasActiveContracts: boolean;
  contracts: ContractOption[];
}

interface ChangeRequestOptions {
  agent: {
    portalUserId: string;
    managedAgentId: string | null;
    email: string;
    displayName: string | null;
    commissionLevel: string | null;
  };
  carriers: CarrierOption[];
  uplineOptions: Array<{ portalUserId: string; name: string; email: string }>;
  commissionLevels: string[];
}

/** One carrier + contract pair chosen in step 1. */
interface SelectionRow {
  key: string;
  carrier: string;
  contractId: string;
}

interface ItemDraft {
  contractId: string;
  plan: string;
  currentUplineName: string | null;
  currentUplineNumber: string | null;
  currentLevel: string | null;
  newUplinePortalUserId: string;
  newUplineName: string;
  newLevel: string;
  editing: boolean;
}

interface CarrierDraft {
  key: string;
  carrier: string;
  label: string;
  contractId: string;
  contractNumber: string | null;
  contractType: DownlineChangeContractType;
  commissionType: DownlineChangeCommissionType;
  items: ItemDraft[];
  open: boolean;
  productsOpen: boolean;
}

let rowSequence = 0;
function nextRowKey() {
  rowSequence += 1;
  return `row-${rowSequence}`;
}

function agentLabel(agent: { displayName: string | null; email: string }) {
  return agent.displayName || agent.email;
}

function contractLabel(contract: ContractOption) {
  const upline = contract.uplineName ? `Under ${contract.uplineName}` : "No upline on file";
  return `${contract.contractNumber ?? ""} - ${upline}`.trim().replace(/^-\s*/, "");
}

function StepIndicator({ step }: { step: 1 | 2 }) {
  const stepTwo = step === 2;
  return (
    <div className="flex items-center px-6 py-2" aria-label={`Step ${step} of 2`}>
      <span
        className={cn(
          "grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 font-display text-lg font-bold",
          stepTwo
            ? "border-status-active-border bg-status-active-border text-white"
            : "border-accent-strong bg-accent-strong text-on-accent"
        )}
        aria-hidden
      >
        1
      </span>
      <span
        className={cn("h-0.5 flex-1", stepTwo ? "bg-status-active-border" : "bg-accent-strong")}
        aria-hidden
      />
      <span
        className={cn(
          "grid h-11 w-11 shrink-0 place-items-center rounded-full border-2 font-display text-lg font-bold",
          stepTwo
            ? "border-accent-strong bg-accent-strong text-on-accent"
            : "border-accent-strong bg-surface-raised text-accent"
        )}
        aria-hidden
      >
        2
      </span>
    </div>
  );
}

function AgentPicker({
  downlines,
  loading,
  selected,
  onSelect,
}: {
  downlines: DownlineRow[];
  loading: boolean;
  selected: DownlineRow | null;
  onSelect: (agent: DownlineRow | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return downlines;
    return downlines.filter((agent) =>
      [agent.displayName ?? "", agent.email, agent.phone ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [downlines, query]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (selected) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border-default bg-surface-input px-4 py-2.5">
        <span className="flex-1 truncate text-sm text-content-primary">{agentLabel(selected)}</span>
        <button
          type="button"
          onClick={() => {
            onSelect(null);
            setQuery("");
          }}
          className="focus-ring rounded p-1 text-content-muted hover:text-content-primary"
          aria-label="Clear selected agent"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeWidth={2} d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Select agent..."
        role="combobox"
        aria-expanded={open}
        aria-controls="downline-agent-options"
        className="focus-ring w-full rounded-xl border border-border-default bg-surface-input px-4 py-2.5 text-sm text-content-primary placeholder:text-content-muted"
      />
      {open && (
        <div
          id="downline-agent-options"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-border-default bg-surface-raised shadow-card"
        >
          <ul className="max-h-64 overflow-y-auto">
            {loading && <li className="px-4 py-3 text-sm text-content-muted">Loading downlines…</li>}
            {!loading && matches.length === 0 && (
              <li className="px-4 py-3 text-sm text-content-muted">No agents found</li>
            )}
            {matches.map((agent) => (
              <li key={agent.id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(agent);
                    setOpen(false);
                  }}
                  className="focus-ring block w-full px-4 py-3 text-left hover:bg-surface-overlay"
                >
                  <span className="block font-semibold text-content-primary">{agentLabel(agent)}</span>
                  <span className="block text-sm text-content-secondary">{agent.email}</span>
                  {agent.phone && (
                    <span className="block text-sm text-content-secondary">{agent.phone}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          {!loading && (
            <p className="border-t border-border-subtle bg-surface-overlay px-4 py-2 text-sm text-content-secondary">
              Total <span className="font-bold text-content-primary">{matches.length}</span> items found
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Two-step change request an upline submits for one of their downlines:
 * pick the agent and their contracts, then say what should change.
 */
export function PortalDownlineChangeRequestPage() {
  const token = getSessionToken();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success: toastSuccess } = useToast();

  const [downlines, setDownlines] = useState<DownlineRow[]>([]);
  const [loadingDownlines, setLoadingDownlines] = useState(true);
  const [agent, setAgent] = useState<DownlineRow | null>(null);
  const [options, setOptions] = useState<ChangeRequestOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selections, setSelections] = useState<SelectionRow[]>([
    { key: nextRowKey(), carrier: "", contractId: "" },
  ]);
  const [step, setStep] = useState<1 | 2>(1);
  const [drafts, setDrafts] = useState<CarrierDraft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    async function loadDownlines() {
      try {
        const response = await fetch(`${API_BASE}/api/agents/me/downlines`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load downlines");
        if (!cancelled) setDownlines(json.data as DownlineRow[]);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load downlines");
        }
      } finally {
        if (!cancelled) setLoadingDownlines(false);
      }
    }
    void loadDownlines();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Deep link from a downline's page preselects them.
  const presetAgentId = searchParams.get("agent");
  useEffect(() => {
    if (!presetAgentId || agent) return;
    const match = downlines.find((row) => row.id === presetAgentId);
    if (match) setAgent(match);
  }, [presetAgentId, downlines, agent]);

  useEffect(() => {
    if (!token || !agent) {
      setOptions(null);
      return;
    }
    let cancelled = false;
    setLoadingOptions(true);
    setError(null);
    async function loadOptions() {
      try {
        const response = await fetch(
          `${API_BASE}/api/agents/me/downlines/${encodeURIComponent(agent!.id)}/change-request-options`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (!response.ok || !json.success) throw new Error(json.error || "Unable to load contracts");
        if (!cancelled) setOptions(json.data as ChangeRequestOptions);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load contracts");
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    }
    void loadOptions();
    return () => {
      cancelled = true;
    };
  }, [token, agent]);

  function updateSelection(key: string, patch: Partial<SelectionRow>) {
    setSelections((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  }

  function carrierOption(slug: string) {
    return options?.carriers.find((option) => option.carrier === slug) ?? null;
  }

  function goToStep2() {
    setError(null);
    const chosen = selections.filter((row) => row.carrier && row.contractId);
    if (chosen.length === 0) {
      setError("Choose a carrier and contract to continue");
      return;
    }
    const seen = new Set<string>();
    for (const row of chosen) {
      const key = `${row.carrier}:${row.contractId}`;
      if (seen.has(key)) {
        setError("The same contract is selected twice");
        return;
      }
      seen.add(key);
    }

    setDrafts(
      chosen.map((row) => {
        const carrier = carrierOption(row.carrier);
        const contract = carrier?.contracts.find((entry) => entry.id === row.contractId) ?? null;
        return {
          key: row.key,
          carrier: row.carrier,
          label: carrier?.label ?? row.carrier,
          contractId: row.contractId,
          contractNumber: contract?.contractNumber ?? null,
          contractType: "NO_CHANGE" as DownlineChangeContractType,
          commissionType: "NO_CHANGE" as DownlineChangeCommissionType,
          open: true,
          productsOpen: true,
          items: [
            {
              contractId: row.contractId,
              plan: carrier?.label ?? row.carrier,
              currentUplineName: contract?.uplineName ?? null,
              currentUplineNumber: contract?.uplineNumber ?? null,
              currentLevel: options?.agent.commissionLevel ?? null,
              newUplinePortalUserId: "",
              newUplineName: "",
              newLevel: "",
              editing: false,
            },
          ],
        };
      })
    );
    setStep(2);
  }

  function updateDraft(key: string, patch: Partial<CarrierDraft>) {
    setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));
  }

  function updateItem(draftKey: string, contractId: string, patch: Partial<ItemDraft>) {
    setDrafts((current) =>
      current.map((draft) =>
        draft.key === draftKey
          ? {
              ...draft,
              items: draft.items.map((item) =>
                item.contractId === contractId ? { ...item, ...patch } : item
              ),
            }
          : draft
      )
    );
  }

  // Commission level changes are staff-only; an upline restructures their team
  // but does not move money.
  const canRequestLevel = canSubmitChangeRequest("UPLINE", "COMMISSION_LEVEL");

  const hasChange = drafts.some(
    (draft) =>
      draft.contractType !== "NO_CHANGE" ||
      draft.commissionType !== "NO_CHANGE" ||
      draft.items.some((item) => item.newUplineName || item.newLevel)
  );

  async function send() {
    if (!token || !agent) return;
    if (!hasChange) {
      setError("Nothing to submit — every carrier is set to “No change”");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/agents/me/downline-change-requests`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          subjectPortalUserId: agent.id,
          carriers: drafts.map((draft) => ({
            carrier: draft.carrier,
            contractId: draft.contractId,
            contractNumber: draft.contractNumber,
            contractType: draft.contractType,
            commissionType: draft.commissionType,
            items: draft.items.map((item) => ({
              contractId: item.contractId,
              plan: item.plan,
              currentUplineName: item.currentUplineName,
              currentUplineNumber: item.currentUplineNumber,
              currentLevel: item.currentLevel,
              newUplineName: item.newUplineName || null,
              newUplinePortalUserId: item.newUplinePortalUserId || null,
              newLevel: item.newLevel || null,
            })),
          })),
        }),
      });
      const json = await response.json();
      if (!response.ok || !json.success) throw new Error(json.error || "Unable to send change request");
      toastSuccess("Change request sent");
      setSubmitted(true);
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Unable to send change request");
    } finally {
      setSubmitting(false);
    }
  }

  const intro = (
    <PortalPageIntro
      titleId="change-request-title"
      title="Change Request"
      breadcrumbs={
        <Breadcrumbs
          items={[
            { label: "Setup", to: "/my-agent" },
            { label: "Downlines", to: "/my-agent/downlines" },
            { label: "Change request", current: true },
          ]}
        />
      }
    />
  );

  if (submitted) {
    return (
      <section className="space-y-8" aria-labelledby="change-request-title">
        {intro}
        <EmptyState
          title="Change request sent"
          description={`Your request for ${agent ? agentLabel(agent) : "your downline"} is with the contracting team. You will see the outcome on your downlines page.`}
          action={
            <Link
              to="/my-agent/downlines"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-xl border border-accent-strong bg-accent-strong px-4 py-2.5 text-sm font-medium text-on-accent hover:brightness-95"
            >
              Back to downlines
            </Link>
          }
        />
      </section>
    );
  }

  return (
    <section className="space-y-8" aria-labelledby="change-request-title">
      {intro}

      <Card padding="lg" className="space-y-2 border-2">
        <p className="text-sm text-content-secondary">
          Request hierarchy or contract changes for one of your downlines using the form below.
          Items with <span className="text-status-rejected-text">*</span> are required.
        </p>
        {!canRequestLevel && (
          <p className="text-sm text-content-secondary">
            Commission level changes are handled by the contracting team — ask them directly.
          </p>
        )}
      </Card>

      <Card padding="none" className="overflow-hidden border-2">
        <div className="border-b border-border-subtle py-6">
          <StepIndicator step={step} />
        </div>

        <div className="space-y-8 p-6">
          {error && (
            <p
              role="alert"
              className="rounded-card border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text"
            >
              {error}
            </p>
          )}

          {step === 1 ? (
            <>
              <div className="space-y-4">
                <h2 className="font-display text-lg font-extrabold text-content-primary">
                  Select the agent
                </h2>
                <div className="grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)] sm:items-center">
                  <Label className="mb-0">
                    Agent <span className="text-status-rejected-text">*</span>
                  </Label>
                  <AgentPicker
                    downlines={downlines}
                    loading={loadingDownlines}
                    selected={agent}
                    onSelect={(next) => {
                      setAgent(next);
                      setSelections([{ key: nextRowKey(), carrier: "", contractId: "" }]);
                    }}
                  />
                </div>
              </div>

              <div className="space-y-4 border-t border-border-subtle pt-6">
                <div>
                  <h2 className="font-display text-lg font-extrabold text-content-primary">
                    Select carriers and contracts
                  </h2>
                  <p className="mt-1 text-sm text-content-secondary">
                    To include additional carriers click the “Add Carrier” button below.
                  </p>
                </div>

                {loadingOptions && (
                  <div className="space-y-3" role="status" aria-busy="true">
                    <LoadingStatus label="Loading contracts" />
                    <Skeleton className="h-12 w-full rounded-xl" />
                  </div>
                )}

                {!loadingOptions &&
                  selections.map((row, index) => {
                    const carrier = carrierOption(row.carrier);
                    return (
                      <div
                        key={row.key}
                        className="grid gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] sm:items-center"
                      >
                        <Label className="mb-0">
                          {index === 0 ? (
                            <>
                              Contract <span className="text-status-rejected-text">*</span>
                            </>
                          ) : (
                            ""
                          )}
                        </Label>
                        <Select
                          value={row.carrier}
                          disabled={!options}
                          aria-label="Choose carrier"
                          onChange={(event) =>
                            updateSelection(row.key, { carrier: event.target.value, contractId: "" })
                          }
                        >
                          <option value="">Choose carrier...</option>
                          {options?.carriers.map((option) => (
                            <option
                              key={option.carrier}
                              value={option.carrier}
                              disabled={!option.hasActiveContracts}
                            >
                              {option.hasActiveContracts
                                ? option.label
                                : `${option.label} – No active contracts`}
                            </option>
                          ))}
                        </Select>
                        <div className="flex items-center gap-2">
                          <Select
                            value={row.contractId}
                            disabled={!carrier}
                            aria-label="Choose contract"
                            onChange={(event) =>
                              updateSelection(row.key, { contractId: event.target.value })
                            }
                            className="flex-1"
                          >
                            <option value="">Choose contract...</option>
                            {carrier?.contracts.map((contract) => (
                              <option key={contract.id} value={contract.id}>
                                {contractLabel(contract)} — Status: {contract.status}
                              </option>
                            ))}
                          </Select>
                          {selections.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setSelections((current) =>
                                  current.filter((entry) => entry.key !== row.key)
                                )
                              }
                              className="focus-ring rounded-lg p-2 text-content-muted hover:text-status-rejected-text"
                              aria-label="Remove carrier"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeWidth={2} d="m6 6 12 12M18 6 6 18" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {!loadingOptions && options && options.carriers.every((option) => !option.hasActiveContracts) && (
                  <p className="text-sm text-content-muted">
                    {agent ? agentLabel(agent) : "This agent"} has no active contracts to change yet.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-3 border-t border-border-subtle pt-6">
                <Button variant="secondary" disabled>
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={!options}
                  onClick={() =>
                    setSelections((current) => [
                      ...current,
                      { key: nextRowKey(), carrier: "", contractId: "" },
                    ])
                  }
                >
                  Add Carrier <span aria-hidden>+</span>
                </Button>
                <Button onClick={goToStep2} disabled={!agent || !options}>
                  Next <span aria-hidden>→</span>
                </Button>
              </div>
            </>
          ) : (
            <>
              {drafts.map((draft) => (
                <article key={draft.key} className="rounded-card border-2 border-border-default">
                  <button
                    type="button"
                    onClick={() => updateDraft(draft.key, { open: !draft.open })}
                    className="focus-ring flex w-full items-center justify-between gap-3 border-b border-border-subtle px-5 py-4 text-left"
                    aria-expanded={draft.open}
                  >
                    <span className="font-semibold text-content-primary">{draft.label}:</span>
                    <span aria-hidden className="text-content-muted">
                      {draft.open ? "–" : "+"}
                    </span>
                  </button>

                  {draft.open && (
                    <div className="space-y-6 p-5">
                      <p className="text-sm text-content-secondary">
                        If you would like to change the contract type or commission type, please
                        select your desired option below. Otherwise, select “No change”.
                      </p>

                      <fieldset>
                        <legend className="text-sm font-bold text-content-primary">Contract Type</legend>
                        <div className="mt-2 flex flex-wrap gap-5">
                          {DOWNLINE_CHANGE_CONTRACT_TYPES.map((value) => (
                            <label key={value} className="flex items-center gap-2 text-sm text-content-primary">
                              <input
                                type="radio"
                                name={`contract-type-${draft.key}`}
                                checked={draft.contractType === value}
                                onChange={() => updateDraft(draft.key, { contractType: value })}
                                className="h-4 w-4 accent-[var(--color-accent-strong)]"
                              />
                              {DOWNLINE_CHANGE_CONTRACT_TYPE_LABELS[value]}
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <fieldset>
                        <legend className="text-sm font-bold text-content-primary">Commission Type</legend>
                        <div className="mt-2 flex flex-wrap gap-5">
                          {DOWNLINE_CHANGE_COMMISSION_TYPES.map((value) => (
                            <label key={value} className="flex items-center gap-2 text-sm text-content-primary">
                              <input
                                type="radio"
                                name={`commission-type-${draft.key}`}
                                checked={draft.commissionType === value}
                                onChange={() => updateDraft(draft.key, { commissionType: value })}
                                className="h-4 w-4 accent-[var(--color-accent-strong)]"
                              />
                              {DOWNLINE_CHANGE_COMMISSION_TYPE_LABELS[value]}
                            </label>
                          ))}
                        </div>
                      </fieldset>

                      <div className="rounded-card border border-border-default">
                        <button
                          type="button"
                          onClick={() => updateDraft(draft.key, { productsOpen: !draft.productsOpen })}
                          className="focus-ring flex w-full items-center justify-between gap-3 border-b border-border-subtle px-4 py-3 text-left"
                          aria-expanded={draft.productsOpen}
                        >
                          <span className="flex items-center gap-3 text-sm font-semibold text-content-primary">
                            <svg className="h-5 w-5 text-content-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m12 3 9 5-9 5-9-5 9-5Zm9 9-9 5-9-5m18 4-9 5-9-5" />
                            </svg>
                            Products
                          </span>
                          <span aria-hidden className="text-content-muted">
                            {draft.productsOpen ? "–" : "+"}
                          </span>
                        </button>

                        {draft.productsOpen && (
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[52rem] text-sm">
                              <thead>
                                <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wider text-content-muted">
                                  <th className="px-4 py-3 font-bold">Carrier Plan</th>
                                  <th className="px-4 py-3 font-bold">Current Upline</th>
                                  <th className="px-4 py-3 font-bold">Current Level</th>
                                  <th className="px-4 py-3 font-bold">New Upline</th>
                                  {canRequestLevel && (
                                    <th className="px-4 py-3 font-bold">New Level</th>
                                  )}
                                  <th className="px-4 py-3 font-bold text-right">
                                    <span className="sr-only">Actions</span>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {draft.items.map((item) => (
                                  <tr key={item.contractId} className="border-b border-border-subtle last:border-0">
                                    <td className="px-4 py-4 text-content-primary">{item.plan}</td>
                                    <td className="px-4 py-4 text-content-secondary">
                                      {item.currentUplineName
                                        ? `${item.currentUplineName}${item.currentUplineNumber ? `: ${item.currentUplineNumber}` : ""}`
                                        : "—"}
                                    </td>
                                    <td className="px-4 py-4 text-content-secondary">
                                      {item.currentLevel ?? "—"}
                                    </td>
                                    <td className="px-4 py-4">
                                      {item.editing ? (
                                        <Select
                                          value={item.newUplinePortalUserId}
                                          aria-label="New upline"
                                          onChange={(event) => {
                                            const choice = options?.uplineOptions.find(
                                              (option) => option.portalUserId === event.target.value
                                            );
                                            updateItem(draft.key, item.contractId, {
                                              newUplinePortalUserId: choice?.portalUserId ?? "",
                                              newUplineName: choice?.name ?? "",
                                            });
                                          }}
                                        >
                                          <option value="">No change</option>
                                          {options?.uplineOptions.map((option) => (
                                            <option key={option.portalUserId} value={option.portalUserId}>
                                              {option.name}
                                            </option>
                                          ))}
                                        </Select>
                                      ) : (
                                        <span className="text-content-secondary">
                                          {item.newUplineName || "—"}
                                        </span>
                                      )}
                                    </td>
                                    {canRequestLevel && (
                                    <td className="px-4 py-4">
                                      {item.editing ? (
                                        <Select
                                          value={item.newLevel}
                                          aria-label="New level"
                                          onChange={(event) =>
                                            updateItem(draft.key, item.contractId, {
                                              newLevel: event.target.value,
                                            })
                                          }
                                        >
                                          <option value="">No change</option>
                                          {options?.commissionLevels.map((level) => (
                                            <option key={level} value={level}>
                                              {level}
                                            </option>
                                          ))}
                                        </Select>
                                      ) : (
                                        <span className="text-content-secondary">
                                          {item.newLevel || "—"}
                                        </span>
                                      )}
                                    </td>
                                    )}
                                    <td className="px-4 py-4">
                                      <div className="flex justify-end gap-2">
                                        <Button
                                          size="sm"
                                          onClick={() =>
                                            updateItem(draft.key, item.contractId, {
                                              editing: !item.editing,
                                            })
                                          }
                                        >
                                          {item.editing ? "Done" : "Edit"}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          onClick={() =>
                                            updateItem(draft.key, item.contractId, {
                                              newUplinePortalUserId: "",
                                              newUplineName: "",
                                              newLevel: "",
                                              editing: false,
                                            })
                                          }
                                        >
                                          Reset
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </article>
              ))}

              <div className="flex flex-wrap justify-end gap-3 border-t border-border-subtle pt-6">
                <Button variant="secondary" onClick={() => setStep(1)} disabled={submitting}>
                  <span aria-hidden>←</span> Previous
                </Button>
                <Button onClick={() => void send()} disabled={submitting}>
                  {submitting ? "Sending…" : "Send"} <span aria-hidden>→</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <button
        type="button"
        onClick={() => navigate("/my-agent/downlines")}
        className="focus-ring rounded text-sm font-semibold text-accent hover:opacity-90"
      >
        Cancel and go back to downlines
      </button>
    </section>
  );
}
